import React, { useState, useEffect, useMemo } from "react";
import MultiSelectDropdown from "../../../components/ui/MultiSelectDropdown";
import LocalFilterPanel from "../../../components/common/LocalFilterPanel";
import TooltipAttributeSettingsField from "../../../components/common/TooltipAttributeSettingsField";
import { makeRequest } from "../../../services/api/client";
import { useOcelMetadata } from "../../../stores/OcelContext";
import {
  SectionHeader,
  ActivityFilterBlock,
  ComparisonSection,
  BetrachtungszeitraumSection,
} from "../_shared/PeriodSettingsControls";

const AggregationKPISettings = ({
  componentTitle,
  onTitleChange,
  objectType,
  onObjectTypeChange,
  selectedAttribute,
  onSelectedAttributeChange,
  tooltipAttributes,
  onTooltipAttributesChange,
  availableObjectTypes,
  availableActivities,
  localFilters,
  updateLocalFilter,
  activeFile,
  loading,
  // Betrachtungszeitraum / Vorperioden-Vergleich
  periodPreset,
  onPeriodPresetChange,
  customStart,
  onCustomStartChange,
  customEnd,
  onCustomEndChange,
  showPeriodLabel,
  onShowPeriodLabelChange,
  comparisonEnabled,
  onComparisonEnabledChange,
  trendDirection,
  onTrendDirectionChange,
}) => {
  const metadata = useOcelMetadata(activeFile);
  const datasetMin = metadata?.date_range?.start ? metadata.date_range.start.split("T")[0] : undefined;
  const datasetMax = metadata?.date_range?.end ? metadata.date_range.end.split("T")[0] : undefined;

  const [scopedNumericAttributes, setScopedNumericAttributes] = useState(null);
  const [attrsLoading, setAttrsLoading] = useState(false);

  useEffect(() => {
    if (activeFile) {
      fetchAttributesForType(objectType || undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAttributesForType = async (type) => {
    if (!activeFile) return;
    setAttrsLoading(true);
    try {
      const data = await makeRequest("/ocel/object-type-attributes", {
        params: { file_type: activeFile, object_type: type || undefined },
      }, "object type attributes");
      setScopedNumericAttributes(data?.numeric_attributes || []);
    } catch {
      setScopedNumericAttributes(null);
    } finally {
      setAttrsLoading(false);
    }
  };

  const attributeOptions = scopedNumericAttributes !== null
    ? scopedNumericAttributes
    : (metadata?.object_attribute_names || []);

  // All object attribute names for the presence filter
  const allObjectAttributes = metadata?.object_attribute_names || [];

  // Tooltip attribute options — scoped to the selected object type via the
  // OCEL schema (mirrors GanttChart's effectiveAttributes/GanttSettings.jsx),
  // falling back to the flat all-types list for "All object types".
  const tooltipAttributeOptions = useMemo(() => {
    if (!objectType) return allObjectAttributes;
    const scoped = (metadata?.object_types_schema || []).find((t) => t.name === objectType);
    const names = (scoped?.attributes || []).map((a) => a.name);
    return names.length > 0 ? names : allObjectAttributes;
  }, [objectType, metadata, allObjectAttributes]);

  const handleObjectTypeChange = (val) => {
    onObjectTypeChange(val);
    updateLocalFilter("objectTypes", val ? [val] : []);
    onSelectedAttributeChange("");
    onTooltipAttributesChange([]);
    setScopedNumericAttributes(null);
    fetchAttributesForType(val || undefined);
  };

  // ── Presence filter helpers ─────────────────────────────────────────────
  const presenceFilter = localFilters?.objectAttributePresenceFilters || { attributeNames: [], enabled: false, mode: 'include' };
  const presenceMode = presenceFilter.mode || 'include';
  const presenceAttributeNames = presenceFilter.attributeNames || [];

  const handlePresenceModeChange = (mode) => {
    updateLocalFilter("objectAttributePresenceFilters", {
      ...presenceFilter,
      mode,
    });
  };

  const handlePresenceAttributesChange = (vals) => {
    updateLocalFilter("objectAttributePresenceFilters", {
      ...presenceFilter,
      attributeNames: vals,
      enabled: vals.length > 0,
    });
  };

  return (
    <div className="space-y-5">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Component Title <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <input
          type="text"
          value={componentTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Aggregation KPI"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Object Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Object Type</label>
        <select
          value={objectType}
          onChange={(e) => handleObjectTypeChange(e.target.value)}
          disabled={loading || availableObjectTypes.length === 0}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-50 disabled:text-gray-400"
        >
          <option value="">All object types</option>
          {availableObjectTypes.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">Scope aggregation to a specific object type</p>
      </div>

      {/* Attribute to aggregate */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Attribute to Aggregate</label>
        {attrsLoading ? (
          <p className="text-xs text-gray-400">Loading attributes…</p>
        ) : (
          <select
            value={selectedAttribute}
            onChange={(e) => onSelectedAttributeChange(e.target.value)}
            disabled={loading || attributeOptions.length === 0}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-50 disabled:text-gray-400"
          >
            <option value="">Select an attribute…</option>
            {attributeOptions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        )}
        <p className="text-xs text-gray-500 mt-1">The numeric attribute to sum across all matching objects</p>
      </div>

      {/* Tooltip-Konfiguration — object attributes shown as a table when hovering
          the main value; optional. Shared field, see TooltipAttributeSettingsField. */}
      <TooltipAttributeSettingsField
        attributeOptions={tooltipAttributeOptions}
        selectedAttributes={tooltipAttributes}
        onSelectedAttributesChange={onTooltipAttributesChange}
        trigger="hover"
        disabled={loading}
      />

      {/* Betrachtungszeitraum */}
      <BetrachtungszeitraumSection
        periodPreset={periodPreset}
        onPeriodPresetChange={onPeriodPresetChange}
        customStart={customStart}
        onCustomStartChange={onCustomStartChange}
        customEnd={customEnd}
        onCustomEndChange={onCustomEndChange}
        datasetMin={datasetMin}
        datasetMax={datasetMax}
        comparisonEnabled={comparisonEnabled}
      />

      {/* Periodenbezeichnung anzeigen */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={showPeriodLabel}
          onChange={(e) => onShowPeriodLabelChange(e.target.checked)}
          disabled={loading}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-gray-700">Periodenbezeichnung anzeigen</span>
      </label>

      {/* Vorperioden-Vergleich */}
      <ComparisonSection
        periodPreset={periodPreset}
        comparisonEnabled={comparisonEnabled}
        onComparisonEnabledChange={onComparisonEnabledChange}
        trendDirection={trendDirection}
        onTrendDirectionChange={onTrendDirectionChange}
        loading={loading}
        radioGroupName="aggregationKpiTrendDirection"
      />

      {/* Activity Filter — within time scope */}
      <div>
        <SectionHeader
          title="Aktivitäten im Betrachtungszeitraum"
          subtitle="Nur Objekte, die die gewählten Aktivitäten innerhalb des oben gewählten Zeitraums ausgeführt haben."
        />
        <div className="mt-3">
          <ActivityFilterBlock
            filterKey="activityFilters"
            localFilters={localFilters}
            updateLocalFilter={updateLocalFilter}
            availableActivities={availableActivities}
            loading={loading}
          />
        </div>
      </div>

      {/* Activity Filter — full lifecycle */}
      <div>
        <SectionHeader
          title="Aktivitäten im Lebenszyklus"
          subtitle="Nur Objekte, die diese Aktivitäten jemals in ihrer gesamten Geschichte hatten — unabhängig vom Betrachtungszeitraum."
        />
        <div className="mt-3">
          <ActivityFilterBlock
            filterKey="lifecycleActivityFilters"
            localFilters={localFilters}
            updateLocalFilter={updateLocalFilter}
            availableActivities={availableActivities}
            loading={loading}
          />
        </div>
      </div>

      {/* Object Attribute Presence Filter */}
      <div>
        <SectionHeader
          title="Objektattribut vorhanden?"
          subtitle={
            presenceMode === 'include'
              ? "Nur Objekte einschließen, die mindestens eines der gewählten Attribute besitzen."
              : "Objekte ausschließen, die mindestens eines der gewählten Attribute besitzen."
          }
        />
        <div className="mt-3 space-y-3">
          {/* Mode toggle */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handlePresenceModeChange('include')}
              className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                presenceMode === 'include'
                  ? "bg-green-600 text-white border-green-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              Einschließen
            </button>
            <button
              type="button"
              onClick={() => handlePresenceModeChange('exclude')}
              className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                presenceMode === 'exclude'
                  ? "bg-red-600 text-white border-red-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              Ausschließen
            </button>
          </div>

          {/* Attribute multiselect */}
          <MultiSelectDropdown
            label="Attribute auswählen:"
            options={allObjectAttributes}
            selectedValues={presenceAttributeNames}
            onSelectionChange={handlePresenceAttributesChange}
            placeholder="Attribute auswählen..."
            disabled={loading || allObjectAttributes.length === 0}
            itemType="attributes"
          />
          {allObjectAttributes.length === 0 && (
            <p className="text-xs text-gray-400">Keine Objektattribute verfügbar.</p>
          )}
        </div>
      </div>

      {/* LocalFilterPanel — analog zu AttributeSumLineChartSettings.jsx eingebunden.
          Nur "objectAttributes" aktiviert: "dateRange" deckt der Betrachtungszeitraum oben
          bereits ab, "activityNodes" wäre ein dritter, redundanter Aktivitätsfilter-
          Mechanismus neben den beiden ActivityFilterBlock-Abschnitten oben. */}
      <div className="border-t border-gray-100 pt-4">
        <LocalFilterPanel
          localFilters={localFilters}
          updateLocalFilter={updateLocalFilter}
          activeFile={activeFile}
          disabled={loading}
          enabledFilterTypes={['objectAttributes']}
        />
      </div>
    </div>
  );
};

export default AggregationKPISettings;
